import { Button } from "@/components/ui/button";
import { ArrowRight, Calculator } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

export function ROICalculatorBanner() {
  const navigate = useNavigate();
  
  return (
    <section className="py-16 px-6">
      <div className="container mx-auto max-w-6xl">
        <motion.div 
          className="grid lg:grid-cols-2 gap-12 items-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Left side - Visual */}
          <div className="relative order-2 lg:order-1 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Background gradient circles */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-primary/10 to-cyan-500/20 rounded-full blur-3xl" />
              <div className="absolute inset-8 bg-gradient-to-tr from-primary/20 to-emerald-500/20 rounded-full blur-2xl" />
              
              {/* Main calculator icon */}
              <div className="relative z-10 flex items-center justify-center h-full">
                <div className="p-8 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/50 shadow-2xl">
                  <Calculator className="w-24 h-24 md:w-32 md:h-32 text-primary" strokeWidth={1.5} />
                </div>
              </div>
              
              {/* Floating accent elements */}
              <motion.div 
                className="absolute top-8 right-8 w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 backdrop-blur-sm"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div 
                className="absolute bottom-12 left-8 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/20 backdrop-blur-sm"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />
            </div>
          </div>
          
          {/* Right side - Text Content & CTA */}
          <div className="space-y-6 order-1 lg:order-2 text-center lg:text-left">
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-foreground leading-tight">
              What can gimmefy do for your{" "}
              <span className="bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent">ROI</span>?
            </h2>
            <p className="text-xl lg:text-2xl text-muted-foreground font-medium">
              Calculate it in real-time
            </p>
            <p className="text-base lg:text-lg text-muted-foreground max-w-lg">
              See exactly how much time and money your team could save with AI-powered content creation. Our interactive calculator shows your potential ROI in minutes.
            </p>
            <Button 
              onClick={() => navigate('/roi-calculator')}
              size="lg"
              variant="hero"
              className="mt-4"
            >
              Calculate Your ROI
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
